/**
 * brewDB feature icon: the "Percolate" mark — a pour-over cone / ETL funnel
 * drips a query through, and table rows accumulate beneath. Two cuts:
 * `full` (gradient funnel + drop + three rows) for 20px and above, `micro`
 * (solid funnel + two rows, no drop) below 20px. Paint modes: `primary`
 * (aurora gradient — dark grounds only), `flat` (brew-blue — light grounds),
 * `mono` (currentColor). Geometry is normative — see
 * docs/superpowers/specs/2026-07-07-brewdb-logo-design.md; do not redraw.
 *
 * The gradient uses a fixed id: instances share the first matching def,
 * and every instance renders an identical gradient, so collisions are
 * harmless (and it keeps the id SSR/React-version agnostic).
 */
const BLUE_LIGHT = '#3CB4E5';
const BLUE = '#007CBA';
const GRAD_ID = 'brewdb-mark-aurora-grad';

type BrewDbIconProps = {
  className?: string;
  cut?: 'full' | 'micro';
  paint?: 'primary' | 'flat' | 'mono';
};

export default function BrewDbIcon({ className, cut = 'full', paint = 'primary' }: Readonly<BrewDbIconProps>) {
  let solid = BLUE_LIGHT;
  if (paint === 'flat') solid = BLUE;
  else if (paint === 'mono') solid = 'currentColor';
  const funnelStroke = paint === 'primary' ? `url(#${GRAD_ID})` : solid;

  return (
    <svg
      viewBox="0 0 64 64"
      height="1em"
      width="1em"
      className={className}
      style={{ verticalAlign: '-0.125em' }}
      aria-hidden="true"
      focusable="false"
    >
      {paint === 'primary' && cut === 'full' && (
        <defs>
          <linearGradient id={GRAD_ID} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#6FD0F2" />
            <stop offset="1" stopColor="#0A86C4" />
          </linearGradient>
        </defs>
      )}
      {cut === 'full' ? (
        <>
          <g fill="none" stroke={funnelStroke} strokeWidth="4.25" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 8 A20 3.6 0 0 1 52 8" />
            <path d="M12 8 L27.5 26 L27.5 32" />
            <path d="M52 8 L36.5 26 L36.5 32" />
          </g>
          <path
            d="M32 34.5 C34 37 35.4 38.9 35.4 40.8 A3.4 3.5 0 1 1 28.6 40.8 C28.6 38.9 30 37 32 34.5 Z"
            fill={solid}
          />
          <g fill={solid}>
            <rect x="22" y="46" width="20" height="3.8" rx="1.9" opacity="0.45" />
            <rect x="18.5" y="52" width="27" height="3.8" rx="1.9" opacity="0.7" />
            <rect x="15" y="58" width="34" height="3.8" rx="1.9" />
          </g>
        </>
      ) : (
        <g fill={solid}>
          <path d="M12 7 L52 7 L36.5 25 L36.5 31 L27.5 31 L27.5 25 Z" stroke={solid} strokeWidth="6" strokeLinejoin="round" />
          <rect x="20" y="41" width="24" height="6.5" rx="3.25" />
          <rect x="16" y="52" width="32" height="6.5" rx="3.25" />
        </g>
      )}
    </svg>
  );
}
